import { getCurrencySymbol } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { PageEvent } from "@shared/angular2-datatable/data-table.directive";
import { Channel, ClickarPath, SessionKey } from "@shared/clickar-constants";
import { RatingSelectorComponent } from "@shared/component/rating-selector/rating-selector.component";
import { EmailDialogComponent } from "@shared/dialogs/email-dialog/email-dialog.component";
import { TableData } from "@shared/model/auction.model";
import { Purchase } from "@shared/model/lotDTO";
import { VehicleDTO } from "@shared/model/vehicle.model";
import { EmailData, RatingData } from "@shared/services/alert-message/alert-message.model";
import { ApiService } from "@shared/services/api.service";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";
import { UsersService } from "@shared/services/users/users.service";
import { ClickarUtilsService } from "@shared/services/utils/clickar-utils.service";
import { WcmService } from "@shared/services/wcm/wcm.service";
import { Subject } from "rxjs";
import { auditTime, switchMap, take } from "rxjs/operators";

@Component({
	selector: "app-shopping-cart",
	templateUrl: "./purchase-summary.component.html",
	styleUrl: "./purchase-summary.component.scss",
	standalone: false
})
export class PurchaseSummaryComponent implements OnInit {
	wcmService = inject(WcmService);
	private apiService = inject(ApiService);
	private router = inject(Router);
	private dialog = inject(MatDialog);
	translate = inject(TranslateService);
	utils = inject(ClickarUtilsService);
	private userService = inject(UsersService);

	protected Math = Math;
	protected readonly Channel = Channel;
	protected readonly ClickarPath = ClickarPath;
	protected getCurrencySymbol = getCurrencySymbol;
	private storageService = inject(StorageManagerService);
	private destroyRef = inject(DestroyRef);
	protected market: string;

	private readonly reload = new Subject<void>();

	purchaseHistory: Purchase[] = [];
	filteredPurchaseHistory: Purchase[] = [];

	/** Acquisti trovati dal server, a prescindere dalla pagina mostrata */
	protected total = 0;
	/**
	 * Finche' e' true chiediamo una pagina per volta e lasciamo ordinare il server.
	 * I filtri lavorano in memoria sull'elenco completo, che pero' in questo stato non abbiamo:
	 * aprendo il pannello filtri passiamo a false e scarichiamo tutto una volta sola.
	 */
	protected serverPaged = true;

	/**
	 * Pagina da cui far partire la tabella, ripresa dalla sessione.
	 * NOTE: non viene piu' aggiornata dopo la creazione. La pagina attiva la possiede la direttiva, che
	 * la cambia anche per conto suo (recalculatePage la riporta all'ultima pagina disponibile) senza
	 * passare da setPage: ribindarla dall'esterno le farebbe perdere il sincrono con il paginator, e i
	 * controlli che riportano indietro diventerebbero no-op.
	 */
	protected initialPage = 1;

	/** Righe da dare alla tabella: la pagina appena arrivata, o l'elenco filtrato in memoria */
	protected get rows(): Purchase[] {
		return this.serverPaged ? this.purchaseHistory : this.filteredPurchaseHistory;
	}

	protected onSingleLevelChange(values: VehicleDTO[] | Purchase[], filtersHaveChanged: boolean) { // NOTE: VehicleDTO serve solo per evitare che Typescript rompa le balle
		this.filteredPurchaseHistory = values as Purchase[];

		if (filtersHaveChanged) { // NOTE: senza questa guardia ogni ricarica dei dati riporterebbe l'utente a pagina 1
			this.storePaging(this.tableData.mfActivePage = 1);
		}
	}

	rowsOnPageSet = [10, 20, 30, 50];
	tableData: TableData = {
		rowsOnPage: 50, // NOTE: e' anche il limit della prima chiamata, che parte quindi con offset=0&limit=50
		orderBy: "datePurchase",
		sortOrder: "desc",
		offset: 0,
		limit: 50,
		col: 21,
		mfActivePage: 1
	};
	showSearch = false;

	constructor() {
		this.tableData.orderBy = this.storageService.getSessionItem(SessionKey.PURCHASE_HISTORY_SORTBY, this.tableData.orderBy || "datePurchase");
		this.tableData.sortOrder = this.storageService.getSessionItem(SessionKey.PURCHASE_HISTORY_SORT_ORDER, this.tableData.sortOrder || "desc");
		Object.assign(this.tableData, this.storageService.getSessionItem(SessionKey.PURCHASE_HISTORY_PAGING, { mfActivePage: this.tableData.mfActivePage ?? 1, rowsOnPage: this.tableData.rowsOnPage ?? 50 }));
		this.initialPage = this.tableData.mfActivePage ?? 1;

		this.userService.getProfileOfLoggedUser()
			.pipe(take(1))
			.subscribe(user => {
				this.market = user.idMarket;
			});

		// NOTE: un click su un sorter emette sia mfSortByChange sia mfSortOrderChange: auditTime accorpa
		// i due eventi in una sola chiamata, switchMap scarta la risposta di una richiesta sorpassata.
		this.reload
			.pipe(
				auditTime(0),
				switchMap(() => this.serverPaged
					? this.apiService.getPurchasesPageableUsingGET(
						((this.tableData.mfActivePage ?? 1) - 1) * (this.tableData.rowsOnPage ?? 50),
						this.tableData.rowsOnPage,
						this.tableData.orderBy as string,
						this.tableData.sortOrder
					)
					// senza offset/limit il server manda tutto, e da li' in poi ordiniamo e filtriamo in memoria
					: this.apiService.getPurchasesPageableUsingGET()
				),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(({ data, total }) => {
				this.total = total;
				this.purchaseHistory = data;

				for (const v of this.purchaseHistory) {
					if (this.utils.isNullOrUndefined(v.auctioneerRating) || v.auctioneerRating === 0) {
						v.auctioneerRating = v.numFeedback = 0;
					}
				}
			});
	}

	ngOnInit() {
		this.getPurchases()
	}

	getPurchases() {
		this.reload.next();
	}

	/**
	 * I filtri hanno bisogno dell'elenco completo, sia per filtrare sia per popolare le proprie tendine,
	 * e il server non li applica: la prima volta che l'utente li apre scarichiamo tutto e restiamo in
	 * memoria, cosi' chi non filtra non paga il costo dello storico intero.
	 */
	protected toggleSearch() {
		if ((this.showSearch = !this.showSearch) && this.serverPaged) {
			this.serverPaged = false;
			this.getPurchases();
		}
	}

	protected saveOrderBy(attr: string | string[]) { // NOTE: string[] just to keep TS quiet
		this.tableData.orderBy = attr as string; // NOTE: casting just to keep TS quiet
		this.storageService.setSessionItem(SessionKey.PURCHASE_HISTORY_SORTBY, attr);
		this.reloadIfServerPaged();
	}

	protected saveSortOrder(order: "asc" | "desc") {
		this.tableData.sortOrder = order;
		this.storageService.setSessionItem(SessionKey.PURCHASE_HISTORY_SORT_ORDER, order);
		this.reloadIfServerPaged();
	}

	/** Ordinamento e paginazione li fa il server solo finche' non ci serve l'elenco completo */
	private reloadIfServerPaged() {
		if (this.serverPaged) {
			this.getPurchases();
		}
	}
	private storePaging = (activePage = this.tableData.mfActivePage, rowsOnPage = this.tableData.rowsOnPage) => {
		this.storageService.setSessionItem(SessionKey.PURCHASE_HISTORY_PAGING, { mfActivePage: activePage, rowsOnPage: rowsOnPage });
	}
	protected savePaging(pageEvent: PageEvent | any) { // Note: any just to satisfy Typescript
		const { activePage, rowsOnPage } = pageEvent as PageEvent,
			/*
			 * NOTE: la direttiva riemette onPageChange a ogni cambio dei dati (recalculatePage, chiamata
			 * sia da ngOnChanges su mfData sia da ngDoCheck quando il differ vede un array nuovo).
			 * Senza questo confronto la risposta di una chiamata ne farebbe partire un'altra, all'infinito.
			 */
			pagingChanged = activePage !== this.tableData.mfActivePage || rowsOnPage !== this.tableData.rowsOnPage;

		this.storePaging( // NOTE: da aggiornare comunque: la direttiva puo' aver ridotto la pagina attiva all'ultima disponibile
			this.tableData.mfActivePage = activePage,
			this.tableData.rowsOnPage = rowsOnPage
		);

		if (pagingChanged) {
			this.reloadIfServerPaged();
		}
	}

	vote(element: any) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.data = new RatingData();
		dialogConfig.data.idAuctioneer = element.idAuctioneer;
		switch (element.channel) {
			case Channel.AUCTION:
				dialogConfig.data.idAuction = element.idAuction;
				break;
			case Channel.SHOWCASE:
				dialogConfig.data.idLot = element.idLot;
		}
		dialogConfig.width = dialogConfig.height = "auto";
		this.dialog.open(RatingSelectorComponent, dialogConfig)
			.afterClosed().subscribe(result => {
				if (result) {
					this.getPurchases()
				}
			});
	}

	awardEmail(element: any) {
		const dialogConfig = new MatDialogConfig(),
			data = new EmailData();

		data.email = element.awardEmail;
		data.recipients = element.recipients;
		data.ccRecipients = element.ccRecipients;
		data.subject = element.subject;
		dialogConfig.data = data;

		dialogConfig.width = dialogConfig.height = "auto";
		this.dialog.open(EmailDialogComponent, dialogConfig);
	}

	goToPresentation(idTemplate: any) {
		this.router.navigate([
			`${ClickarPath.PRIVATE}/${ClickarPath.PARTNER}`,
			{ idTemplate }
		]);
	}

	payByLink(element: any) {
		this.apiService.getLink(element.idPurchase)
			.subscribe(value => {
				window.open(value.link, "_self");
			});
	}
}